import { formatHumanDate } from '@/lib/date-display.js';
import { formatCurrency } from '@/lib/format.js';
import OpenDeal from './OpenDeal.jsx';
import { leadRenewal } from './renewal-lead.js';
import { SESSION_STATUS_WORDS, PILOT_ACTION_WORDS, PENDING_PILOT_WORDS, LEAD_NO_QUOTES, LEAD_NO_AIRCRAFT, NO_PILOT_FORMS, NOT_RECORDED, word } from './words.js';

// THE RENEWAL LEAD (2026-10-05): on an account with a renewal in progress, the page opens with what the broker acts on. It reads
// the same pending term the Terms section renders (renewal-lead.js leadRenewal over payload.pending_terms) and the term that
// reader says it renews; nothing is derived here. It has no Start button: the renewal is started, and the way into the deal is
// Open deal. Who and what is renewed, the expiring term and its date, the renewal's status, each pilot form with what is waiting
// on whom, quotes received and presented, aircraft.
function Line({ label, children, id }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 border-b border-border py-2 last:border-b-0" data-lead-line={id}>
      <span className="w-24 shrink-0 text-xs font-medium text-text-muted">{label}</span>
      <span className="min-w-0 flex-1 text-sm text-text">{children}</span>
    </div>
  );
}

export default function RenewalLead({ data, account }) {
  const lead = leadRenewal(data);
  if (!lead) return null;
  const { pending: p, term, pilots, outstanding, aircraft } = lead;
  const q = p.quotes || null;
  const started = p.started_on ? formatHumanDate(p.started_on) : null;
  const expiring = term ? (term.period?.expiration_date || null) : (p.renews?.expiration_date || null);
  return (
    <section className="rounded-card border border-accent/50 bg-surface" data-renewal-lead data-pending-id={p.id}>
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border px-4 py-2.5">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">Renewal in progress</span>
        <span className="text-[15px] font-semibold text-text" data-lead-who>{data.account?.name || NOT_RECORDED}</span>
        <span className="ml-auto"><OpenDeal account={account} crmDealId={p.crm_deal_id} /></span>
      </header>
      <div className="px-4">
        <Line label="Renewing" id="term">
          {term ? <>{term.carrier?.name || 'carrier not stated'}{term.policy_number ? <span className="ml-2 font-mono text-xs text-text-muted">{term.policy_number}</span> : null}</> : <span className="text-text-dim" data-state-word>the expiring term is not on this page</span>}
          {expiring ? <span className="ml-2">expires <span className="font-mono" data-lead-expiring>{formatHumanDate(expiring)}</span></span> : null}
        </Line>
        <Line label="Status" id="status">
          <span data-lead-status={p.status}>{word(SESSION_STATUS_WORDS, p.status)}</span>
          <span className="ml-2 text-text-muted">{started ? `started ${started}` : 'start date not recorded'}</span>
          {pilots.length ? <span className="ml-2 text-text-muted" data-lead-outstanding={outstanding}>{outstanding ? `${outstanding} of ${pilots.length} pilot ${pilots.length === 1 ? 'form' : 'forms'} outstanding` : 'every pilot form attested'}</span> : null}
        </Line>
        <Line label="Pilots" id="pilots">
          {pilots.length ? (
            <span className="flex flex-col gap-1" data-lead-pilots>
              {pilots.map((pp) => (
                <span key={pp.phf_id} className="flex flex-wrap items-baseline gap-x-2" data-lead-pilot={pp.state}>
                  <span className="font-medium">{pp.name || NOT_RECORDED}</span>
                  <span className="text-xs text-text-muted">{word(PENDING_PILOT_WORDS, pp.state)}</span>
                  <span className="text-xs text-text-dim" data-lead-pilot-action>{word(PILOT_ACTION_WORDS, pp.state)}</span>
                </span>
              ))}
            </span>
          ) : <span className="text-text-dim" data-state-word>{NO_PILOT_FORMS}</span>}
        </Line>
        <Line label="Quotes" id="quotes">
          {q && q.count ? <span data-lead-quotes={q.count}>{q.count} received, {q.presented} presented{q.low_premium != null ? `, low ${formatCurrency(q.low_premium)} ${q.low_carrier || ''}` : ''}</span> : <span className="text-text-muted" data-lead-quotes="0">{LEAD_NO_QUOTES}</span>}
        </Line>
        <Line label="Aircraft" id="aircraft">
          {aircraft.length ? (
            <span className="flex flex-col gap-0.5" data-lead-aircraft>
              {aircraft.map((a) => <span key={a.id}><span className="font-mono">{a.registration || NOT_RECORDED}</span> <span className="text-text-muted">{[a.year, a.make, a.model].filter(Boolean).join(' ')}</span></span>)}
            </span>
          ) : <span className="text-text-dim" data-state-word>{LEAD_NO_AIRCRAFT}</span>}
        </Line>
      </div>
    </section>
  );
}
